import ProgressTrackerChip from "@/app/components/cards/ProgressTrackerChip";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { userHasAdminRights } from "@/lib/utils/pageChecks";
import { Carousel } from "@mantine/carousel";
import { Stack, useComputedColorScheme } from "@mantine/core";
import { Flashcard } from "@tsTypes/flash-schema";
import clsx from "clsx";
import { getUserRole } from "lib/utils/accessChecks";
import { useSession } from "next-auth/react";
import { Dispatch, SetStateAction, useState } from "react";
import {
  flashcardInteraction,
  selectFlashcardById,
} from "../../../../../../../../../features/progress/subTopicUserReduxMetadataSlice";
import {
  useCustomDispatch,
  useCustomSelector,
} from "../../../../../../../../../lib/redux/hooks";
import ResourceEditInStrapiButton from "../../../../questions/[topic]/components/Header/ResourceEditInStrapiButton";
import CardButtons from "./buttons/CardButtons";
import classes from "./FlashCard.module.css";
import { default as FlashCardContent } from "./FlashCardContent";

type FlashCardProps = {
  deckDocumentId: string;
  reviewCards: string[];
  flashcard: Flashcard;
  subjectNameLower: string;
  subjectDocumentId: string;
  topicDocumentId: string;
  currentSubTopicDocumentId: string | string[];
  reversed: boolean;
  setAttempted: Dispatch<SetStateAction<string[]>>;
  disableActions: boolean;
};

export default function FlashCard({
  deckDocumentId,
  reviewCards,
  flashcard,
  subjectNameLower,
  topicDocumentId,
  subjectDocumentId,
  currentSubTopicDocumentId,
  reversed,
  setAttempted,
  disableActions,
}: FlashCardProps) {
  const { data: session } = useSession();
  const userRole = getUserRole(session);
  const userHasAdminEditAccess =
    userHasAdminRights(userRole) || userRole === "Content Creator";
  const [front, setFront] = useState<boolean>(true);
  const subjectColorIndex = getSubjectColorIndex(subjectNameLower);
  const { style } = useFlashcardStyles({ subjectColorIndex, front });
  const saved = reviewCards.includes(flashcard.documentId);
  const dispatch = useCustomDispatch();
  // meta
  const subTopicDocumentId =
    typeof currentSubTopicDocumentId === "string"
      ? currentSubTopicDocumentId
      : currentSubTopicDocumentId.find((id) =>
          flashcard.RelatedSubTopics.some((s) => s.documentId === id)
        )!;
  const cardMeta = useCustomSelector((state) =>
    selectFlashcardById(
      state,
      subjectDocumentId,
      topicDocumentId,
      subTopicDocumentId,
      flashcard.documentId
    )
  );

  // handlers
  function setFrontHandler(state: boolean) {
    setFront(state);
    setAttempted((currentAttempted: string[]) => {
      if (!currentAttempted.includes(flashcard.documentId)) {
        return [...currentAttempted, flashcard.documentId];
      }
      return currentAttempted;
    });
  }
  function handleFlashcardAttempt(isStarAction: boolean) {
    const lastAttempted = cardMeta?.dv[cardMeta.dv.length - 1];
    const lastStarred = cardMeta?.ds[cardMeta.ds.length - 1];
    // if either:
    // never attempted, or last attempt >1m ago
    // never starred, or last star > 1m ago
    // log attempted/starred
    if (
      !isStarAction
        ? !lastAttempted || Date.now() - lastAttempted > 1000 * 60 * 1
        : !lastStarred || Date.now() - lastStarred > 1000 * 60 * 1
    ) {
      dispatch(
        flashcardInteraction({
          subjectDocumentId: subjectDocumentId,
          topicDocumentId: topicDocumentId,
          subTopicDocumentId: subTopicDocumentId,
          cardDocumentId: flashcard.documentId,
          isStarAction: isStarAction,
          date: Date.now(),
        })
      );
    }
  }
  return (
    <Carousel.Slide
      className={clsx(classes.card, !front && classes.flipped)}
      style={style}
    >
      {flashcard && (
        <CardButtons
          subjectDocumentId={subjectDocumentId}
          deckDocumentId={deckDocumentId}
          handleFlashcardAttempt={handleFlashcardAttempt}
          cardDocumentId={flashcard.documentId}
          front={front}
          saved={saved}
          disableActions={disableActions}
        />
      )}
      <Stack
        className={clsx(classes.cardSide, front ? classes.front : classes.back)}
        onClick={() => {
          setFrontHandler(!front);
          if (!front || disableActions) {
            return;
          }
          // save flashcard attempt when flipping to back
          handleFlashcardAttempt(false);
        }}
        gap={0}
      >
        <FlashCardContent
          content={
            !reversed
              ? front
                ? flashcard.Front
                : flashcard.Back
              : front
              ? flashcard.Back
              : flashcard.Front
          }
        />
      </Stack>
      <span
        style={{
          position: "absolute",
          bottom: "10px",
          right: "10px",
        }}
      >
        <ProgressTrackerChip
          resource="flashcards"
          disableActions={disableActions}
          subjectName={subjectNameLower}
          subjectDocumentId={subjectDocumentId}
          resourceReduxMeta={cardMeta}
          noResource={!flashcard}
        />
      </span>
      {userHasAdminEditAccess && (
        <span
          style={{
            position: "absolute",
            bottom: "10px",
            left: "10px",
          }}
        >
          <ResourceEditInStrapiButton
            resource={"flashcards"}
            resourceDocumentId={flashcard.documentId}
          />
        </span>
      )}
    </Carousel.Slide>
  );
}

export const useFlashcardStyles = ({
  subjectColorIndex,
}: {
  subjectColorIndex?: string;
  front: Boolean;
  side?: "front" | "back";
}) => {
  const colorScheme = useComputedColorScheme("light");
  const isDark = colorScheme === "dark";

  const borderColor = subjectColorIndex
    ? isDark
      ? `var(--mantine-color-${subjectColorIndex}-3)`
      : `color-mix(in srgb, var(--mantine-color-${subjectColorIndex}-0) 85%, black 15%)`
    : isDark
    ? "var(--mantine-color-lightblue-7)"
    : "var(--mantine-color-darkblue-6)";

  const frontBg = isDark
    ? "var(--mantine-color-dark-7)"
    : subjectColorIndex
    ? `color-mix(in srgb, var(--mantine-color-${subjectColorIndex}-0) 10%, white 90%)`
    : "var(--mantine-color-darkblue-8)";

  const backBg = isDark
    ? "var(--mantine-color-dark-9)"
    : subjectColorIndex
    ? `var(--mantine-color-${subjectColorIndex}-0)`
    : "var(--mantine-color-lightblue-1)";

  const style = {
    "--fc-border-color": borderColor,
    "--fc-front-bg": frontBg,
    "--fc-back-bg": backBg,
  } as React.CSSProperties;

  return { classes, style };
};
